"use client";

import { useEffect, useRef, useState } from "react";
import { LuSearch, LuX } from "react-icons/lu";

export type SearchBarProps = {
  /** The committed value reflected in the URL. Used to seed the
   *  internal text state on mount and on external resets. */
  value: string;
  /** Called after the debounce window with the new committed value. */
  onChange: (value: string) => void;
  placeholder?: string;
  /** Debounce in ms before calling onChange. Defaults to 200ms. */
  debounceMs?: number;
};

/** A controlled-but-debounced search input. The user types instantly into
 *  local state; we wait `debounceMs` of quiet before bubbling the value
 *  upward (which then writes to the URL). */
export function SearchBar({
  value,
  onChange,
  placeholder = "Search by name, country, expertise, or service...",
  debounceMs = 200,
}: SearchBarProps) {
  const [text, setText] = useState(value);
  const [prevValue, setPrevValue] = useState(value);
  const lastEmitted = useRef(value);

  // Sync from external value (e.g. when URL changes from another control,
  // browser back/forward, or a Clear-all action). Adjusting state while
  // rendering — the React-recommended alternative to a setState-in-effect.
  // Only adopt the external value when it differs from the current text, so an
  // in-progress edit isn't clobbered by our own echoed value.
  if (value !== prevValue) {
    setPrevValue(value);
    if (value !== text) {
      setText(value);
    }
  }

  // Track the last externally-supplied value so the debounce below doesn't echo
  // a value we just adopted back to the parent. Ref writes belong in an effect.
  useEffect(() => {
    lastEmitted.current = value;
  }, [value]);

  // Debounce text -> onChange
  useEffect(() => {
    if (text === lastEmitted.current) return;
    const handle = window.setTimeout(() => {
      lastEmitted.current = text;
      onChange(text);
    }, debounceMs);
    return () => window.clearTimeout(handle);
  }, [text, debounceMs, onChange]);

  const clear = () => {
    setText("");
    lastEmitted.current = "";
    onChange("");
  };

  return (
    <div className="relative flex-1 min-w-[220px]">
      <LuSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        aria-label="Search"
        className="w-full pl-9 pr-9 py-2 rounded-xl bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
      />
      {text.length > 0 && (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear search"
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
        >
          <LuX className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
